// Where an inserted template lands in a reply box (docs/decisions.md,
// 2026-10-06). One rule for every box (main draft, per-step message boxes,
// closed-conversation reply box; eBay and email):
//   1. the agent placed the cursor in the box since its content was last set
//      programmatically -> insert AT THE CURSOR, replacing any selection;
//   2. otherwise -> BELOW the existing body, ABOVE the signature;
//   3. no signature found at the end (e.g. the agent edited it) -> at the end,
//      as before.
// Pure functions only, so the rule is unit-testable with node:test. The
// signature is always the conversation's live, resolved signature (what the
// panel's signatureSeed() returns), never text invented here; the eBay one-line
// forms are also still recognized through templateMerge.js's signatureLineRe().

import { normalizeForSignatureCompare, templateHtmlToPlainText } from '@cxhub/shared'
import { signatureLineRe, stripTrailingSignature, mergeTemplateContent } from './templateMerge.js'
import { hasRichFormatting, plainTextToEditableHtml, toEditableHtml, applySubstitutions, applySubstitutionsHtml } from './richTextConversion.js'

const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', '#39': "'" }

// normalizeForSignatureCompare() (shared, the same comparison
// isSignatureOnlyReply() uses), plus entity decoding: the editor's own text
// is decoded, a stored HTML signature template is not.
export function signatureCompareText(value) {
  return normalizeForSignatureCompare(value)
    .replace(/&(#39|[a-z]+);/gi, (m, name) => ENTITIES[name.toLowerCase()] ?? m)
    .replace(/\s+/g, ' ')
    .trim()
}

function signatureTexts(signatures) {
  return [...new Set((signatures ?? []).map(signatureCompareText).filter(Boolean))]
}

// Index of the first of the trailing blocks whose combined text is the
// signature, or -1. blockTexts: the plain text of each top-level block
// (paragraph), in order. The shortest matching run wins, so a signature that
// happens to repeat earlier in the body is never mistaken for the start.
export function findSignatureBlockStart(blockTexts, signatures) {
  const sigs = signatureTexts(signatures)
  if (!sigs.length) return -1
  const texts = (blockTexts ?? []).map(signatureCompareText)
  let acc = ''
  for (let i = texts.length - 1; i >= 0; i--) {
    acc = texts[i] ? (acc ? `${texts[i]} ${acc}` : texts[i]) : acc
    if (texts[i] && sigs.includes(acc)) return i
    if (sigs.every((s) => acc.length > s.length)) return -1
  }
  return -1
}

const VOID_TAGS = new Set(['br', 'img', 'hr', 'input', 'meta', 'link', 'wbr', 'col', 'area', 'base', 'source', 'embed', 'track'])

// Splits HTML into its top-level elements (text at the top level becomes its
// own block). Enough for the editor's own output and a stored template: no
// scripts or comments to worry about.
export function splitTopLevelHtmlBlocks(html) {
  const s = String(html ?? '')
  const blocks = []
  const tagRe = /<\/?([a-z][a-z0-9]*)\b[^>]*?(\/?)>/gi
  let depth = 0
  let start = 0
  let m
  while ((m = tagRe.exec(s))) {
    const name = m[1].toLowerCase()
    const closing = m[0][1] === '/'
    const selfClosing = m[2] === '/' || VOID_TAGS.has(name)
    if (depth === 0 && m.index > start && s.slice(start, m.index).trim()) {
      blocks.push(s.slice(start, m.index))
      start = m.index
    }
    if (depth === 0) start = m.index
    if (closing) depth = Math.max(0, depth - 1)
    else if (!selfClosing) depth++
    if (depth === 0) {
      blocks.push(s.slice(start, tagRe.lastIndex))
      start = tagRe.lastIndex
    }
  }
  if (s.slice(start).trim()) blocks.push(s.slice(start))
  return blocks
}

export function htmlBlockText(block) {
  return block.replace(/<br\b[^>]*>/gi, ' ')
}

// A blank line above the signature in every blank email/reply box
// (2026-10-12), so the agent can click there to type or insert a template.
// An email box gets one empty paragraph; an eBay (plain-text) box one blank
// line, still plain text, since that box is exactly what is sent. Never
// content: isSignatureOnlyReply() compares text with tags stripped and
// whitespace collapsed, so "blank line + signature" still counts as empty.
export const BLANK_LINE_HTML = '<p></p>'
export function withBlankLineAboveSignature(signature, { plain = false } = {}) {
  if (!signature) return ''
  return plain ? `\n${signature}` : `${BLANK_LINE_HTML}${signature}`
}

// Whether every block above index `at` (the signature's first block) is
// empty: the box holds only the seeded blank line(s) and the signature.
export function onlyBlankAbove(blockTexts, at) {
  if (at <= 0) return at === 0
  return (blockTexts ?? []).slice(0, at).every((t) => !signatureCompareText(t))
}

// Rule 2/3 for an email (rich editor) box, on the box's value: the template's
// blocks go immediately before the trailing signature run, or at the end when
// there is no signature. A blank box becomes just the template. Used by the
// panel only when the editor itself can't do the insert (the editor does the
// same thing on its own document, see RichReplyEditor.jsx).
export function insertHtmlTemplate(existing, templateHtml, signatures) {
  if (!signatureCompareText(existing)) return templateHtml
  const blocks = splitTopLevelHtmlBlocks(toEditableHtml(existing))
  const texts = blocks.map(htmlBlockText)
  const at = findSignatureBlockStart(texts, signatures)
  if (at < 0) return `${blocks.join('')}${templateHtml}`
  // Only the seeded blank line above the signature: the template takes its
  // place, with no empty paragraph left between it and the signature.
  if (onlyBlankAbove(texts, at)) return `${templateHtml}${blocks.slice(at).join('')}`
  return `${blocks.slice(0, at).join('')}${templateHtml}${blocks.slice(at).join('')}`
}

// The HTML an email box receives for a template. A rich template keeps its
// own HTML; a plain one (trivial tags only) is converted to editable
// paragraphs with its own trailing signature line stripped. Never the
// one-line "[AGENT_FIRST_NAME] | 1A Auto" footer: an email box ends with the
// live signature only (docs/decisions.md, 2026-09-28/29).
export function emailTemplateHtml({ content, substitutedPlain, account, substituteHtml = (h) => h }) {
  if (hasRichFormatting(content)) return substituteHtml(content)
  return plainTextToEditableHtml(stripTrailingSignature(substitutedPlain, account))
}

// A template's HTML for an email (rich editor) box, substitutions applied:
// the agent's first name, the order's reference id, the tracking link. The
// reply box and the New view (2026-10-08) both insert through this.
// A plain template is laid out exactly as the Templates editor shows it
// (2026-10-28, @cxhub/shared's templateHtmlToPlainText(), then
// plainTextToEditableHtml(): a paragraph a paragraph, a line break a <br>),
// never doubled.
export function emailTemplateInsertHtml(content, { account, firstName = '', erpOrderId = null, trackingUrl = null } = {}) {
  const substituted = applySubstitutions(templateHtmlToPlainText(content), { firstName, erpOrderId, trackingUrl })
  return emailTemplateHtml({
    content, substitutedPlain: substituted, account,
    substituteHtml: (h) => applySubstitutionsHtml(h, { firstName, erpOrderId, trackingUrl }),
  })
}

// Offset where the trailing signature starts in a plain-text (eBay) box, or
// -1. An exact live signature first, then the one-line forms.
export function findPlainSignatureStart(text, { account, signatures } = {}) {
  const t = String(text ?? '').replace(/\s+$/, '')
  for (const sig of (signatures ?? []).map((x) => String(x ?? '').trim()).filter(Boolean)) {
    if (t.endsWith(sig)) {
      const at = t.length - sig.length
      if (at === 0 || t[at - 1] === '\n') return at
    }
  }
  const m = signatureLineRe(account).exec(t)
  if (!m) return -1
  return m[0].startsWith('\n') ? m.index + 1 : m.index
}

function stripTemplateSignature(template, account, signatures) {
  let t = stripTrailingSignature(template, account)
  const at = findPlainSignatureStart(t, { account, signatures })
  if (at >= 0) t = t.slice(0, at)
  return t.trim()
}

// A plain-text (eBay) box: returns { text, cursor }, where cursor is the
// offset just after the inserted template. The template's own trailing
// signature is always stripped and the box never ends up with two.
//   cursor: { start, end } when the agent placed the cursor (rule 1), else null.
//   footer: the signature to add when the box has none (the main eBay box and
//           step boxes always did); null where none was added before.
export function insertPlainTemplate(existing, template, { account, signatures, cursor = null, footer = null } = {}) {
  const current = String(existing ?? '')
  const tpl = stripTemplateSignature(template, account, signatures)
  if (cursor) {
    const start = Math.max(0, Math.min(cursor.start ?? 0, current.length))
    const end = Math.max(start, Math.min(cursor.end ?? start, current.length))
    let text = `${current.slice(0, start)}${tpl}${current.slice(end)}`
    if (footer && findPlainSignatureStart(text, { account, signatures }) < 0) text = `${text.replace(/\s+$/, '')}\n\n${footer}`
    return { text, cursor: start + tpl.length }
  }
  const at = findPlainSignatureStart(current, { account, signatures })
  if (at >= 0) {
    const body = current.slice(0, at).replace(/\s+$/, '')
    const prefix = body.trim() ? `${body}\n\n` : ''
    return { text: `${prefix}${tpl}\n\n${current.slice(at).trim()}`, cursor: prefix.length + tpl.length }
  }
  const merged = mergeTemplateContent(current, tpl, account)
  return { text: footer ? `${merged}\n\n${footer}` : merged, cursor: merged.length }
}

// Whether the agent's last recorded cursor still applies: the box holds the
// same value it held when the cursor was recorded. Any programmatic set (a
// suggestion loaded, a reset after send, a signature seed) changes the value
// and so forgets the cursor.
export function placedCursor(record, currentValue) {
  if (!record || record.value !== (currentValue ?? '')) return null
  return { start: record.start, end: record.end }
}
